export interface Ledge {
  kind: 'ledge';
  y: number;
  left: number;
  right: number;
}

export interface Wall {
  kind: 'wall';
  x: number;
  top: number;
  bottom: number;
  side: 'left' | 'right';
}

export type Surface = Ledge | Wall;

export const SURFACE_EPS = 2;
/** Sprites carry transparent padding below the feet; sink by this so the
 *  visible feet meet the surface instead of hovering above it. */
export const FEET_DROP_PX = 4;

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface MeasureInput {
  content: Rect;
  petWidth: number;
  perches: Rect[];
  sidebarEdge: { x: number; top: number; bottom: number } | null;
  /** Where the companion prefers to be, when that place is on screen. */
  favoured: { left: number; right: number } | null;
}

export const restY = (ledge: Ledge, petH: number): number =>
  ledge.y - petH + FEET_DROP_PX;

export const overlapsX = (a: Ledge, b: Ledge): boolean =>
  Math.min(a.right, b.right) > Math.max(a.left, b.left) + SURFACE_EPS;

/**
 * The highest surface at or below the sprite's feet under its current x — what
 * it stands on, or what it would fall onto. Falls back to the floor, which is
 * always the first ledge.
 */
export function resolveSurface(
  ledges: Ledge[],
  x: number,
  y: number,
  petH: number,
): Ledge {
  const feet = y + petH;
  // A seated sprite sits FEET_DROP_PX *below* its surface line, so the snap
  // tolerance must clear the drop as well as the epsilon — otherwise a
  // correctly-seated companion fails to recognise the ledge it is standing on
  // and falls through it.
  const tolerance = SURFACE_EPS + FEET_DROP_PX;
  let best: Ledge | null = null;

  for (const ledge of ledges) {
    if (x < ledge.left - SURFACE_EPS || x > ledge.right + SURFACE_EPS) continue;
    if (ledge.y >= feet - tolerance && (!best || ledge.y < best.y)) best = ledge;
  }

  return best ?? ledges[0]!;
}

export function measureSurfaces({
  content,
  petWidth,
  perches,
  sidebarEdge,
}: MeasureInput): { ledges: Ledge[]; walls: Wall[] } {
  const floor: Ledge = {
    kind: 'ledge',
    y: content.bottom,
    left: content.left,
    right: content.right - petWidth,
  };

  const walkablePerches = perches.filter((rect) => rect.right - petWidth > rect.left);

  const perchLedges = walkablePerches
    .map((rect): Ledge => ({
      kind: 'ledge',
      y: rect.top,
      left: rect.left,
      right: rect.right - petWidth,
    }));

  const perchWalls: Wall[] = walkablePerches.flatMap((rect): Wall[] => [
    { kind: 'wall', x: rect.left, top: rect.top, bottom: rect.bottom, side: 'left' },
    { kind: 'wall', x: rect.right, top: rect.top, bottom: rect.bottom, side: 'right' },
  ]);

  const sidebarWall: Wall[] = sidebarEdge
    ? [{ kind: 'wall', x: sidebarEdge.x, top: sidebarEdge.top, bottom: sidebarEdge.bottom, side: 'left' }]
    : [];

  return { ledges: [floor, ...perchLedges], walls: [...sidebarWall, ...perchWalls] };
}
